import React, { useEffect, useRef, useState } from "react";
import EmojiPicker from "emoji-picker-react";

export default function MeetChatPanel({
  canPublishData,
  userNameMap,
  chatMessages = [],
  onMessageSend,
}) {
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [chatMessages.length]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiRef.current && !emojiRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onEmojiClick = (emojiData) => {
    setMessage((prev) => prev + emojiData.emoji);
  };

  const [panelShow, setPanelShow] = useState(false);

  return (
    <>
      {!panelShow && (
        <div className="gm-chat-shell">
          <div className="gm-chat-panel">
            <div className="gm-chat-header">
              <div className="gm-chat-headerLeft">
                <div className="gm-chat-headerIcon">
                  <i className="fa-solid fa-comments"></i>
                </div>

                <div>
                  <div className="gm-chat-title">In-call messages</div>

                  <div className="gm-chat-subtitle">
                    Messages are visible to everyone
                  </div>
                </div>
              </div>

              <button
                onClick={() => setPanelShow(true)}
                className={`jt-mobileDrawerClose ${panelShow}`}
              >
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div className="gm-chat-messages">
              {chatMessages.length === 0 ? (
                <div className="gm-chat-empty">
                  <div className="gm-chat-emptyIcon">
                    <i className="fa-regular fa-comments"></i>
                  </div>

                  <div className="gm-chat-emptyTitle">No messages yet</div>

                  <div className="gm-chat-emptyText">
                    Send a message to everyone in the meeting
                  </div>
                </div>
              ) : (
                chatMessages.map((m, idx) => {
                  const identity = m.from?.identity || "";

                  const displayName =
                    (identity && userNameMap?.[identity]) ||
                    m.from?.name ||
                    (() => {
                      const match = /^user_[a-f\d]{24}_(.+)$/i.exec(identity);
                      return match?.[1] || identity || "Unknown";
                    })() ||
                    "Unknown";

                  const initials = displayName?.[0]?.toUpperCase();

                  return (
                    <div
                      key={`${identity}-${m.timestamp?.getTime?.() ?? idx}`}
                      className="gm-chat-messageRow"
                    >
                      <div className="gm-chat-avatar">{initials}</div>

                      <div className="gm-chat-messageContent">
                        <div className="gm-chat-messageTop">
                          <span className="gm-chat-messageName">
                            {displayName}
                          </span>

                          <span className="gm-chat-messageTime">
                            {m.timestamp
                              ? new Date(m.timestamp).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : ""}
                          </span>
                        </div>

                        <div className="gm-chat-messageText">{m.message}</div>
                      </div>
                    </div>
                  );
                })
              )}

              <div ref={messagesEndRef} />
            </div>

            <form
              className="gm-chat-inputArea"
              onSubmit={(e) => {
                e.preventDefault();

                if (!canPublishData || !message.trim()) return;

                onMessageSend?.(message);

                setMessage("");
              }}
            >
              <div className="gm-chat-inputWrap">
                {/* Emoji Button */}
                <div className="gm-emoji-wrapper" ref={emojiRef}>
                  <i
                    className="fa-regular fa-face-smile gm-chat-inputIcon"
                    onClick={() => setShowEmojiPicker((prev) => !prev)}
                  ></i>

                  {showEmojiPicker && (
                    <div className="gm-emoji-picker">
                      <EmojiPicker onEmojiClick={onEmojiClick} />
                    </div>
                  )}
                </div>

                <input
                  className="gm-chat-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    canPublishData
                      ? "Send a message to everyone"
                      : "Chat disabled for this table"
                  }
                  disabled={!canPublishData}
                />

                <button
                  className="gm-chat-sendBtn"
                  type="submit"
                  disabled={!canPublishData || !message.trim()}
                >
                  <i className="fa-solid fa-paper-plane"></i>
                </button>
              </div>
            </form>

            {!canPublishData && (
              <div className="gm-chat-disabledText">
                You don&apos;t have permission to send messages in this session.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
